import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Search, CalendarPlus, MapPin, Calendar, Mail, Phone, Building, User, Trash2, Eye } from 'lucide-react';
import { toast } from 'sonner';

const API = '/api';

export const AdminCourseScheduleRequestsPage = () => {
  const { t, language } = useLanguage();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);

  useEffect(() => {
    fetchRequests();
  }, [statusFilter]);

  const fetchRequests = async () => {
    try {
      let url = `${API}/admin/course-schedule-requests`;
      if (statusFilter && statusFilter !== 'all') {
        url += `?status=${statusFilter}`;
      }
      const response = await fetch(url, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setRequests(data);
      }
    } catch (error) {
      console.error('Error fetching requests:', error);
      toast.error('Failed to load requests');
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (requestId, newStatus) => {
    try {
      const response = await fetch(`${API}/admin/course-schedule-requests/${requestId}/status?status=${newStatus}`, {
        method: 'PUT',
        credentials: 'include',
      });

      if (response.ok) {
        toast.success('Status updated');
        fetchRequests();
      } else {
        toast.error('Failed to update status');
      }
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleDelete = async (requestId) => {
    if (!window.confirm('Are you sure you want to delete this request?')) return;

    try {
      const response = await fetch(`${API}/admin/course-schedule-requests/${requestId}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      if (response.ok) {
        toast.success('Request deleted');
        fetchRequests();
      } else {
        toast.error('Failed to delete request');
      }
    } catch (error) {
      toast.error('Failed to delete request');
    }
  };

  const openDetailDialog = (request) => {
    setSelectedRequest(request);
    setDetailDialogOpen(true);
  };

  const filteredRequests = requests.filter(req => 
    req.full_name?.toLowerCase().includes(search.toLowerCase()) ||
    req.course_name?.toLowerCase().includes(search.toLowerCase()) ||
    req.email?.toLowerCase().includes(search.toLowerCase()) ||
    req.preferred_city?.toLowerCase().includes(search.toLowerCase())
  );

  const getStatusColor = (status) => {
    switch (status) {
      case 'new': return 'bg-blue-100 text-blue-800';
      case 'contacted': return 'bg-yellow-100 text-yellow-800';
      case 'scheduled': return 'bg-green-100 text-green-800';
      case 'declined': return 'bg-red-100 text-red-800';
      case 'closed': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric',
        year: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="p-6" data-testid="admin-course-schedule-requests-page">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-[#111827]">Course Schedule Requests</h1>
          <p className="text-[#6B7280]">Requests for different cities or dates</p>
        </div>
        <Badge className="bg-[#33AADD] text-white">
          {requests.filter(r => r.status === 'new').length} New Requests
        </Badge>
      </div>

      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute start-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#6B7280]" />
              <Input
                placeholder="Search by name, course, email, or city..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="ps-9"
                data-testid="search-input"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="All Statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="new">New</SelectItem>
                <SelectItem value="contacted">Contacted</SelectItem>
                <SelectItem value="scheduled">Scheduled</SelectItem>
                <SelectItem value="declined">Declined</SelectItem>
                <SelectItem value="closed">Closed</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#33AADD] mx-auto"></div>
            </div>
          ) : filteredRequests.length === 0 ? (
            <div className="text-center py-8">
              <CalendarPlus className="h-12 w-12 text-[#E5E7EB] mx-auto mb-3" />
              <p className="text-[#6B7280]">No schedule requests found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Contact</TableHead>
                    <TableHead>Course</TableHead>
                    <TableHead>Preferred City</TableHead>
                    <TableHead>Preferred Dates</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Submitted</TableHead>
                    <TableHead className="text-end">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredRequests.map((request, index) => (
                    <TableRow key={request.request_id} data-testid={`request-row-${index}`}>
                      <TableCell>
                        <div>
                          <p className="font-medium text-[#111827]">{request.full_name}</p>
                          <p className="text-sm text-[#6B7280]">{request.email}</p>
                          {request.company && (
                            <p className="text-xs text-[#9CA3AF]">{request.company}</p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="font-medium">{request.course_name}</span>
                      </TableCell>
                      <TableCell>
                        {request.preferred_city || request.preferred_city_other ? (
                          <div className="flex items-center gap-1 text-[#6B7280]">
                            <MapPin className="h-3 w-3" />
                            {request.preferred_city_other || request.preferred_city}
                          </div>
                        ) : '-'}
                      </TableCell>
                      <TableCell>
                        {request.preferred_start_date ? (
                          <div className="text-sm">
                            <div>{formatDate(request.preferred_start_date)}</div>
                            {request.preferred_end_date && (
                              <div className="text-[#6B7280]">to {formatDate(request.preferred_end_date)}</div>
                            )}
                          </div>
                        ) : '-'}
                      </TableCell>
                      <TableCell>
                        <Select 
                          value={request.status} 
                          onValueChange={(v) => updateStatus(request.request_id, v)}
                        >
                          <SelectTrigger className="w-32 h-8">
                            <Badge className={getStatusColor(request.status)}>
                              {request.status}
                            </Badge>
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="new">New</SelectItem>
                            <SelectItem value="contacted">Contacted</SelectItem>
                            <SelectItem value="scheduled">Scheduled</SelectItem>
                            <SelectItem value="declined">Declined</SelectItem>
                            <SelectItem value="closed">Closed</SelectItem>
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell className="text-sm text-[#6B7280]">
                        {formatDate(request.created_at)}
                      </TableCell>
                      <TableCell className="text-end">
                        <Button variant="ghost" size="sm" onClick={() => openDetailDialog(request)}>
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDelete(request.request_id)}>
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Detail Dialog */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Schedule Request Details</DialogTitle>
          </DialogHeader>
          {selectedRequest && (
            <div className="space-y-4">
              <div className="p-4 bg-[#F9FAFB] rounded-lg">
                <p className="text-sm text-[#6B7280] mb-1">Course</p>
                <p className="font-semibold text-[#111827]">{selectedRequest.course_name}</p>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div className="flex items-start gap-3">
                  <User className="h-5 w-5 text-[#33AADD] mt-0.5" />
                  <div>
                    <p className="text-sm text-[#6B7280]">Contact Name</p>
                    <p className="font-medium">{selectedRequest.full_name}</p>
                  </div>
                </div>
                {selectedRequest.company && (
                  <div className="flex items-start gap-3">
                    <Building className="h-5 w-5 text-[#33AADD] mt-0.5" />
                    <div>
                      <p className="text-sm text-[#6B7280]">Company</p>
                      <p className="font-medium">{selectedRequest.company}</p>
                    </div>
                  </div>
                )}
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div className="flex items-start gap-3">
                  <Mail className="h-5 w-5 text-[#33AADD] mt-0.5" />
                  <div>
                    <p className="text-sm text-[#6B7280]">Email</p>
                    <a href={`mailto:${selectedRequest.email}`} className="font-medium text-[#33AADD] hover:underline">
                      {selectedRequest.email}
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Phone className="h-5 w-5 text-[#33AADD] mt-0.5" />
                  <div>
                    <p className="text-sm text-[#6B7280]">Phone</p>
                    <a href={`tel:${selectedRequest.phone}`} className="font-medium text-[#33AADD] hover:underline">
                      {selectedRequest.phone}
                    </a>
                  </div>
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div className="flex items-start gap-3">
                  <MapPin className="h-5 w-5 text-[#33AADD] mt-0.5" />
                  <div>
                    <p className="text-sm text-[#6B7280]">Preferred City</p>
                    <p className="font-medium">
                      {selectedRequest.preferred_city_other || selectedRequest.preferred_city || 'Not specified'}
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Calendar className="h-5 w-5 text-[#33AADD] mt-0.5" />
                  <div>
                    <p className="text-sm text-[#6B7280]">Preferred Dates</p>
                    <p className="font-medium">
                      {selectedRequest.preferred_start_date ? (
                        <>
                          {formatDate(selectedRequest.preferred_start_date)}
                          {selectedRequest.preferred_end_date && ` — ${formatDate(selectedRequest.preferred_end_date)}`}
                        </>
                      ) : 'Not specified'}
                    </p>
                  </div>
                </div>
              </div>

              {selectedRequest.notes && (
                <div className="p-4 bg-[#F9FAFB] rounded-lg">
                  <p className="text-sm text-[#6B7280] mb-1">Additional Notes</p>
                  <p className="text-[#374151] whitespace-pre-wrap">{selectedRequest.notes}</p>
                </div>
              )}

              <div className="flex justify-between items-center pt-4 border-t">
                <div className="text-sm text-[#6B7280]">
                  Submitted: {formatDate(selectedRequest.created_at)}
                </div>
                <div className="flex gap-2">
                  <a href={`mailto:${selectedRequest.email}`}>
                    <Button variant="outline" size="sm">
                      <Mail className="h-4 w-4 me-2" />
                      Send Email
                    </Button>
                  </a>
                  <Select 
                    value={selectedRequest.status} 
                    onValueChange={(v) => {
                      updateStatus(selectedRequest.request_id, v);
                      setSelectedRequest({...selectedRequest, status: v});
                    }}
                  >
                    <SelectTrigger className="w-32">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="new">New</SelectItem>
                      <SelectItem value="contacted">Contacted</SelectItem>
                      <SelectItem value="scheduled">Scheduled</SelectItem>
                      <SelectItem value="declined">Declined</SelectItem>
                      <SelectItem value="closed">Closed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminCourseScheduleRequestsPage;
